<template>
  <Form
    id="credits-form-1"
    ref="proceedToPaymentForm"
    v-slot="{ errors, setErrors, handleSubmit }"
    :initial-values="initialValues"
    @submit="proceedToPayment">
    <div
      :class="[isMobile ? 'scrollbar-none' : 'scrollbar-thin']"
      class="grid grid-flow-col auto-cols-max overflow-auto -mx-1 mb-4 pt-3">
      <CreditsPlansItem
        v-for="(item, index) in sortedProducts"
        :key="item.id"
        :tabindex="index"
        class="h-[8rem] sm:h-34 w-[100px] sm:w-[115px] m-1 relative rounded-lg flex flex-col justify-between cursor-pointer"
        :class="[
          item.id === selectedProductId ? 'shadow-as-border' : 'shadow-as-border-gray',
        ]"
        :item="item"
        :selected-product-id="selectedProductId"
        :handle-submit="handleSubmit"
        :loading="loading"
        :currency="currency"
        @click="selectTariff(item, handleSubmit, $event)"/>

      <div class="w-1"/>
    </div>

    <p
      v-if="priceWithTaxNote"
      class="text-xs text-gray-600 -mt-2 text-right mb-4">
      {{ priceWithTaxNote }}
    </p>

    <YupTooltip
      v-if="!paymentMethods.length"
      :i18n="errors.terms">
      <div class="flex items-start mb-1 mt-5">
        <div class="h-5 flex items-center">
          <Field
            id="terms"
            type="checkbox"
            name="terms"
            class="mt-1.5 h-4 w-4 text-primary border border-primary rounded focus:ring-blue-300"
            :value="true"
            :rules="rules.terms"
            @focus="setErrors(fieldsToReset)"/>
        </div>

        <div class="ml-3 text-sm">
          <label
            for="terms"
            class="font-medium text-gray-600 text-caption leading-4">
            <VRuntimeTemplate
              :template="$t('user.credits.terms_agreement', { terms_of_service: termsOfService, privacy_policy: privacyPolicy })"/>
          </label>
        </div>
      </div>
    </YupTooltip>

    <AppPaymentEmailForm
      v-if="!user.email"
      ref="emailForm"
      class="my-4"/>

    <div :class="{ 'mt-8': !errorMessage }">
      <template v-if="!loading">
        <AlertMessage
          v-if="isPending"
          type="warning"
          class="mb-2.5"
          heading="Transaction pending, check later or contact online support"/>

        <AlertMessage
          v-else-if="successMessage"
          type="success"
          class="mb-3"
          :heading="successMessage"/>

        <AlertMessage
          v-else-if="isFailed || errorMessage"
          type="error"
          class="mb-3"
          :heading="errorMessage || 'Previous transaction failed'"
          :text="errorDescription"/>

        <AlertMessage
          v-else-if="showWarnUpdatingCard"
          type="info"
          class="-mt-3 mb-3"
          heading="When making a payment, you will have to update your card details once more!"/>
      </template>

      <button
        :disabled="loading || isPending || !selectedProduct"
        type="submit"
        class="btn btn-block btn-primary mb-3 h-[44px]">
        <span v-show="!loading">
          {{ $t('user.credits.proceed_to_payment') }}
        </span>

        <Spinner v-show="loading"/>
      </button>

      <button
        v-if="paymentMethods.length"
        type="button"
        class="btn btn-block btn-secondary mb-3"
        :disabled="loading || isPending || !selectedProduct"
        @click="payWithNewCard">
        {{ $t('pay_with_new_card') }}
      </button>

      <button
        v-if="showLegalInformationButton"
        v-smooth-scroll
        type="button"
        class="btn btn-block mb-3 text-sm !text-gray-600 font-medium"
        href="#legal-information-1">
        {{ $t('user.credits.legal_information') }}
      </button>
    </div>
  </Form>
</template>

<script>
import { mapActions, mapState, mapWritableState } from 'pinia'
import { usePaymentStore } from '@/stores/payment'
import { Field, Form } from 'vee-validate'
import { filter, round } from 'lodash'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import YupTooltip from '@/components/Functional/YupTooltip.vue'
import Spinner from '@/components/Functional/Spinner.vue'
import AlertMessage from '@/components/Functional/AlertMessage.vue'
import uniqid from 'uniqid'
import AppPaymentEmailForm from '@/components/App/AppPaymentEmailForm.vue'
import { useAuthStore } from '@/stores/auth'
import CreditsPlansItem from '@/components/Credits/CreditsPlans/Includes/CreditsPlansItem.vue'
import { calcPriceWithTaxNote, calcExpectedPriceInCents } from '@/helpers/tax-helpers.js'

export default {
  name: 'Credits',
  components: {
    YupTooltip,
    Field,
    Form,
    Spinner,
    AlertMessage,
    AppPaymentEmailForm,
    CreditsPlansItem
  },
  props: {
    showLegalInformationButton: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close'],
  data () {
    return {
      errorMessage: null,
      errorDescription: null,
      successMessage: null,
      constantLoading: true,
      loading: false,
      initialValues: {
        terms: false
      },
      fieldsToReset: {
        terms: false
      },
      rules: {
        terms: this.$yup.boolean()
          .required({ key: 'yup.custom.terms_and_conditions', values: {} })
          .oneOf([true], () => ({ key: 'yup.custom.terms_and_conditions', values: {} }))
      },
      // Sorted products
      sortedProducts: []
    }
  },
  computed: {
    ...mapState(useAppStore, [
      'isMobile',
      'currentTime',
      'endlessPaymentCreated'
    ]),
    ...mapState(usePaymentStore, [
      'activePaymentMethod',
      'paymentMethods',
      'products',
      'currency',
      'creditsPayment',
      'showWarnUpdatingCard'
    ]),
    ...mapWritableState(usePaymentStore, [
      'selectedProductId',
      'pathBeforeBuying'
    ]),
    ...mapState(useUserStore, [
      'premiumSupportAgentUserId',
      'user'
    ]),
    ...mapState(useAuthStore, [
      'options'
    ]),
    secondsLeftFromPayment () {
      if (this.creditsPayment) {
        return this.currentTime - this.creditsPayment.createdAt
      }
      else {
        return null
      }
    },
    isPending () {
      return this.creditsPayment && ['created', 'payment_pending', 'payment_accepted'].includes(this.creditsPayment.status)
    },
    isFailed () {
      return this.creditsPayment && ['failed', 'cancelled'].includes(this.creditsPayment.status)
    },
    isLoading () {
      return !this.products.length || this.constantLoading
    },
    termsOfService () {
      return `<a
                href="${window.one2fan.origin}/terms"
                target="_blank"
                class="text-primary">
                {{ $t("common.terms_of_service") }}
              </a>`
    },
    privacyPolicy () {
      return `<a
                href="${window.one2fan.origin}/policy"
                target="_blank"
                class="text-primary">
                {{ $t('common.privacy_policy') }}
              </a>`
    },
    selectedProduct () {
      return this.products.find((item) => item.id === this.selectedProductId)
    },
    priceInCurrency () {
      return this.selectedProduct?.priceInCurrency || null
    },
    priceWithTaxNote () {
      return calcPriceWithTaxNote(this.$t, this.priceInCurrency)
    },
    expectedPriceInCents () {
      return calcExpectedPriceInCents(this.priceInCurrency)
    }
  },
  watch: {
    products: {
      immediate: true,
      handler (products) {
        const selectedProduct = products.find((item) => item.id === this.selectedProductId)

        if (selectedProduct) {
          this.sortedProducts = filter(products, (item) => item.id !== this.selectedProductId)
          this.sortedProducts.unshift(selectedProduct)
        } else {
          this.selectedProductId = null
          this.sortedProducts = products
        }
      }
    },
    creditsPayment (payment) {
      if (payment?.status === 'succeeded') {
        this.purchaseDelete(payment.purchaseId)

        this.successMessage = 'Payment successful'

        setTimeout(() => {
          this.$emit('close')
        }, 1500)
      }
      else if (['failed', 'cancelled'].includes(payment?.status)) {
        this.errorMessage = payment?.error || 'Payment failed'
        this.purchaseDelete(payment.purchaseId)
      }
    }
  },
  created () {
    setTimeout(() => {
      this.constantLoading = false
    }, 500)
  },

  async mounted () {
    if (this.creditsPayment) {
      const purchaseId = this.creditsPayment.purchaseId
      await this.purchaseGet(purchaseId)

      if (this.creditsPayment.status === 'created') {
        await this.purchaseCancel(purchaseId)
          .then(() => {
            this.purchaseDelete(purchaseId)
          })
      }
    }
  },
  methods: {
    ...mapActions(usePaymentStore, [
      'paymentMethodsGet',
      'checkoutSessionCreate',
      'purchase',
      'purchaseGet',
      'purchaseCreate',
      'purchaseDelete',
      'purchaseCancel'
    ]),
    round (value) {
      if (!value) {
        return ''
      }

      return round(value, 2)
    },
    validateSelectedProduct () {
      if (this.selectedProduct) {
        return true
      }

      this.selectedProductId = null
      this.loading = false

      return false
    },
    async selectTariff (tariff, handleSubmit, event) {
      if (!this.loading && !this.isPending) {
        if (this.selectedProductId === null || this.selectedProductId !== tariff.id) {
          this.selectedProductId = tariff.id
        }
        else {
          await handleSubmit(event, this.proceedToPayment)
        }
      }
    },
    async buyCredits () {
      if (!this.validateSelectedProduct()) {
        return
      }

      if (this.$route.name === 'Chat') {
        this.pathBeforeBuying = this.$route.path
      }

      // If card saved - buy immediately
      if (this.paymentMethods.length) {
        if (this.isPending) {
          return
        }

        // Remove failed purchase if payment initialized again
        if (['failed', 'cancelled'].includes(this.creditsPayment?.status)) {
          this.purchaseDelete(this.creditsPayment.purchaseId)
        }

        // Check payment status or init one
        if (this.creditsPayment) {
          await this.purchaseGet(this.creditsPayment.purchaseId)
        }
        else {
          await this.purchaseCreate({ id: `credits_${this.selectedProductId}_${uniqid()}` })
        }

        if (this.endlessPaymentCreated) {
          await new Promise(resolve => setTimeout(resolve, 1000 * 5))
          this.loading = false
          return
        }

        return this.purchase({
          productType: 'credits',
          productId: this.selectedProductId,
          paymentMethodId: this.activePaymentMethod.id,
          successUrl: `${window.origin}/checkout/result?is=success&type=credits&cost=${this.selectedProductId}`,
          purchaseId: this.creditsPayment.purchaseId,
          expectedPriceInCents: this.expectedPriceInCents
        })
          .then(async () => {
            this.errorMessage = null
            this.errorDescription = null
          })
          .catch((error) => {
            if (error?.code === 147) {
              this.errorMessage = error?.message
              this.errorDescription = error?.description
            }
            // Payment is pending
            else if (error?.code === 149) {
              //
            }
            else if (error?.code === 151) {
              this.errorMessage = 'Residence tax error, please refresh the page and try again'
            }
            else {
              this.errorMessage = 'Error occured'
            }

            // Remove payment
            if ([147, 105, 151].includes(error?.code)) {
              this.purchaseDelete(this.creditsPayment.purchaseId)
            }
          })
          .finally(async () => {
            if (this.creditsPayment) {
              await this.purchaseGet(this.creditsPayment.purchaseId)

              // If purchase not completed -> wait n seconds to show loading
              if (!['succeeded', 'failed', 'cancelled'].includes(this.creditsPayment?.status)) {
                await new Promise(resolve => setTimeout(resolve, 1000 * 10))
              }
            }

            this.loading = false
          })
      }
      else {
        await this.payWithNewCard()
      }
    },
    async payWithNewCard () {
      if (!this.validateSelectedProduct()) {
        return
      }

      try {
        this.loading = true

        await this.checkoutSessionCreate({
          productType: 'credits',
          productId: this.selectedProductId,
          successUrl: `${window.origin}/checkout/result?is=success&type=credits&cost=${this.selectedProductId}`,
          cancelUrl: `${window.origin}/checkout/result?is=cancel&type=credits`,
          savePaymentMethod: true,
          expectedPriceInCents: this.expectedPriceInCents
        })
      } catch (error) {
        this.loading = false

        if (error.code === 151) {
          this.errorMessage = 'Residence tax error, please refresh the page and try again'
        } else {
          this.errorMessage = 'An error occurred while creating the checkout session'
        }
      }
    },
    async proceedToPayment () {
      if (!this.loading && !this.isPending) {
        if (!this.validateSelectedProduct()) {
          return
        }

        this.loading = true

        // Check email
        if (!this.user.email) {
          const hasEmail = await this.$refs.emailForm.checkEmail()

          if (!hasEmail) {
            this.loading = false
            return
          }
        }

        // Check if payment method is exists and get if not
        if (!this.activePaymentMethod?.id) {
          await this.paymentMethodsGet()
        }

        await this.buyCredits()
      }
    }
  }
}
</script>

<style scoped>
  /* https://codepen.io/doodack/pen/mBcIf */
  .shadow-hack:before {
    @apply rounded-lg;
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    box-shadow: 0 4px 34px #C9C9D6;
  }
</style>
